import { useContext, useEffect, useMemo, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import DataContext from "../../../context/DataContext";
import {
  useFetch,
  usePermissions,
  useToolsRunTrackedWells,
} from "../../../hooks";
import { usePaginator } from "../../../hooks/usePaginator";
import {
  ClientProps,
  ToolsRunLineItemProps,
} from "../../../interfaces/interfaces";
import { ToolsRunStatusBadge } from "../../../components/documents/toolsRun/ToolsRunStatusBadge";
import { ToolsRunUploadForm } from "../../../components/documents/toolsRun/ToolsRunUploadForm";
import { ToolsRunResolveDialog } from "../../../components/documents/toolsRun/ToolsRunResolveDialog";
import { ToolsRunTimelineDrawer } from "../../../components/documents/toolsRun/ToolsRunTimelineDrawer";
import { formatToolsRunQuantity } from "../../../components/documents/toolsRun/toolsRunFormatters";
import { DocumentsTopNavbar } from "../../../components/documents/DocumentsTopNavbar";
import {
  ListPageLayout,
  ListColumn,
  ListFilterField,
} from "../../../components/shared/ListPageLayout";

interface TrackerResponse {
  success?: boolean;
  succes?: boolean;
  data: ToolsRunLineItemProps[];
}

interface Props {
  clientMode?: boolean;
  assignedClientOptions?: ClientProps[];
  assignedClientId?: number | null;
  onAssignedClientChange?: (clientId: number | null) => void;
}

const formatDate = (value: string | null) => {
  if (!value) return "—";
  const [year, month, day] = value.slice(0, 10).split("-");
  return `${month}/${day}/${year}`;
};

const reasonLabel = (value: string | null) =>
  value
    ? value
        .split(",")
        .map((reason) => reason.replaceAll("_", " "))
        .join(" · ")
    : "";

const STATUS_OPTIONS = [
  { value: "matched", label: "Matched" },
  { value: "needs_review", label: "Needs review" },
  { value: "error", label: "Error" },
];

export const ToolsRunAdmin = ({
  clientMode = false,
  assignedClientOptions = [],
  assignedClientId = null,
  onAssignedClientChange,
}: Props) => {
  const { paginationModel, setPaginationModel, resetPage } =
    usePaginator("tools-run");
  const {
    clientOptions: globalClientOptions,
    fetchDataClient,
    fetchDataProducts,
  } = useContext(DataContext);
  const { wellOptions, fetchDataWells } = useToolsRunTrackedWells();
  const clientOptions = clientMode
    ? assignedClientOptions
    : globalClientOptions;
  const { canManageToolsRun } = usePermissions();
  const [clientId, setClientId] = useState("");
  const [wellId, setWellId] = useState("");
  const [invoice, setInvoice] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [resolveRow, setResolveRow] = useState<ToolsRunLineItemProps | null>(
    null,
  );
  const [timelineRow, setTimelineRow] = useState<ToolsRunLineItemProps | null>(
    null,
  );

  useEffect(() => {
    if (!clientMode) {
      fetchDataClient();
      fetchDataProducts();
    }
    // DataContext actions are not referentially stable; master data only needs loading on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientMode]);

  useEffect(() => {
    if (!clientMode || !assignedClientId) return;
    const selectedId = String(assignedClientId);
    setClientId(selectedId);
    setWellId("");
    fetchDataWells(assignedClientId);
    // DataContext actions are not referentially stable; rerun only when assigned scope changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientMode, assignedClientId]);

  useEffect(() => {
    if (!clientId || !wellId) setTimelineRow(null);
  }, [clientId, wellId]);

  const fetchFilters = useMemo(
    () => ({
      clientId,
      wellId,
      invoice,
      productSearch,
      status,
      dateFrom,
      dateTo,
    }),
    [clientId, wellId, invoice, productSearch, status, dateFrom, dateTo],
  );
  const { data, isLoading, error, fetchData, pagination } =
    useFetch<TrackerResponse>(
      "tools-run-paginate",
      paginationModel,
      undefined,
      undefined,
      fetchFilters,
    );
  const rows = data?.data || [];

  const chooseClient = (value: string) => {
    const nextClientId =
      clientMode && !value && assignedClientId
        ? String(assignedClientId)
        : value;
    setClientId(nextClientId);
    setWellId("");
    if (clientMode) {
      onAssignedClientChange?.(nextClientId ? Number(nextClientId) : null);
    }
    if (nextClientId) fetchDataWells(Number(nextClientId));
    // Client lives in toolbarActions, not the `filters` array, so
    // ListPageLayout's own filterSignature effect won't reset the page for
    // it — reset explicitly here, same as ReportsAdmin's chooseClient.
    resetPage();
  };

  // Single shared reset used both by the upload-completion handler below and
  // (indirectly, via each field's onChange) by ListPageLayout's own built-in
  // "Clear filters" button — there is exactly one place that clears filter
  // state, instead of duplicated per-field resets.
  const clearFilters = () => {
    setWellId("");
    setInvoice("");
    setProductSearch("");
    setStatus("");
    setDateFrom("");
    setDateTo("");
    setTimelineRow(null);
    resetPage();
  };

  const columns: ListColumn<ToolsRunLineItemProps>[] = [
    {
      key: "date",
      header: "Date",
      render: (row) => formatDate(row.date),
      width: "110px",
    },
    {
      key: "invoice",
      header: "Invoice",
      render: (row) => (
        <Typography variant="body2" fontWeight={800}>
          {row.invoice || "—"}
        </Typography>
      ),
      width: "120px",
    },
    {
      key: "clientWell",
      header: "Client / well",
      render: (row) => (
        <>
          <Typography variant="body2" fontWeight={750}>
            {row.client?.name || row.rawShipToClient || "—"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.well?.name || row.rawShipToWell || "—"}
          </Typography>
        </>
      ),
      width: "1.1fr",
    },
    {
      key: "tool",
      header: "Tool",
      render: (row) => (
        <>
          <Typography variant="body2" fontWeight={750}>
            {row.product?.name || row.toolNameRaw || "—"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.product?.partNumber || row.partNumber || "—"}
          </Typography>
        </>
      ),
      width: "1.3fr",
    },
    {
      key: "qty",
      header: "Qty",
      render: (row) => formatToolsRunQuantity(row.qty ?? row.qtyRaw),
      width: "80px",
    },
    {
      key: "status",
      header: "Status",
      render: (row) => (
        <Box>
          <ToolsRunStatusBadge status={row.status} />
          {row.status !== "matched" ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                mt: 0.65,
                textTransform: "capitalize",
                maxWidth: 190,
              }}
            >
              {reasonLabel(row.errorReason || row.reviewReason)}
            </Typography>
          ) : null}
        </Box>
      ),
      width: "1.1fr",
    },
  ];

  // Status is only shown to internal users, matching PumpModuleAdmin/COIAdmin
  // which hide their own Status filter from clientMode users the same way.
  // Well is a genuine narrowing filter (not the persistent Client scope), so
  // it stays in `filters` as an autocomplete-kind ListFilterField.
  const filters: ListFilterField[] = [
    {
      kind: "autocomplete",
      key: "wellId",
      label: "Well",
      value: wellId,
      onChange: setWellId,
      options: wellOptions.map((well) => ({
        value: String(well.id),
        label: well.name,
      })),
      placeholder: clientId ? "Search wells…" : "Select a client",
      disabled: !clientId,
    },
    {
      kind: "text",
      key: "invoice",
      label: "Invoice",
      value: invoice,
      onChange: setInvoice,
      placeholder: "Invoice…",
    },
    ...(!clientMode
      ? [
          {
            kind: "select" as const,
            key: "status",
            label: "Status",
            value: status,
            onChange: setStatus,
            options: STATUS_OPTIONS,
          },
        ]
      : []),
    {
      kind: "date",
      key: "dateFrom",
      label: "From date",
      value: dateFrom,
      onChange: setDateFrom,
    },
    {
      kind: "date",
      key: "dateTo",
      label: "To date",
      value: dateTo,
      onChange: setDateTo,
    },
  ];

  const list = (
    <ListPageLayout<ToolsRunLineItemProps>
      title="Tools Tracker"
      columns={columns}
      rows={rows}
      getRowId={(row) => row.id}
      rowsLoading={isLoading}
      isError={Boolean(error)}
      errorMessage="Tools Run records could not be loaded."
      emptyTitle="No shipment rows found"
      emptySubtitle={
        clientMode
          ? "Adjust the filters to find tool activity."
          : "Adjust the filters or import an Excel workbook."
      }
      searchValue={productSearch}
      onSearchChange={(value) => setProductSearch(value.trim())}
      searchPlaceholder="Search tool or part number…"
      filters={filters}
      page={paginationModel.page}
      pageSize={paginationModel.pageSize}
      totalCount={Number(pagination.totalRecords || 0)}
      onPageChange={(page) => setPaginationModel({ ...paginationModel, page })}
      onPageSizeChange={(pageSize) => setPaginationModel({ page: 0, pageSize })}
      toolbarActions={
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: "10px" }}>
          <Box sx={{ width: 220 }}>
            <Box
              component="label"
              sx={{
                display: "block",
                mb: "5px",
                color: "#9CA1AB",
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: ".6px",
                lineHeight: 1.2,
                textTransform: "uppercase",
              }}
            >
              Client
            </Box>
            <Autocomplete
              size="small"
              options={clientOptions}
              value={
                clientOptions.find(
                  (client) => String(client.id) === clientId,
                ) || null
              }
              getOptionLabel={(client) => client.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, client) =>
                chooseClient(client ? String(client.id) : "")
              }
              disableClearable={clientMode && clientOptions.length === 1}
              disabled={clientMode && clientOptions.length === 1}
              autoHighlight
              openOnFocus
              noOptionsText="No clients found"
              sx={{
                "& .MuiOutlinedInput-root": {
                  minHeight: 38,
                  bgcolor: "#FAFAF8",
                  borderRadius: "9px",
                  fontSize: 13,
                  "& fieldset": { borderColor: "#E2E2DD" },
                  "&:hover fieldset": { borderColor: "#C9C7BE" },
                  "&.Mui-focused fieldset": {
                    borderColor: "#ED8B00",
                    borderWidth: "1px",
                  },
                },
                "& .MuiInputBase-input": { py: "8px" },
              }}
              renderInput={(params) => (
                <TextField {...params} placeholder="Search clients…" />
              )}
            />
          </Box>
          {canManageToolsRun ? (
            <Button
              variant="outlined"
              startIcon={<UploadFileRoundedIcon />}
              onClick={() => setUploadOpen(true)}
              sx={{
                borderColor: "#E7E7E3",
                color: "#2A2F38",
                borderRadius: "9px",
                px: "15px",
                py: "8px",
                fontSize: 13,
                fontWeight: 600,
                textTransform: "none",
                "&:hover": { borderColor: "#ED8B00", color: "#ED8B00" },
              }}
            >
              Import Excel
            </Button>
          ) : null}
        </Box>
      }
      rowActions={(row) => (
        <>
          {clientId &&
          wellId &&
          String(row.clientId) === clientId &&
          String(row.wellId) === wellId &&
          row.productId ? (
            <Button
              size="small"
              startIcon={<TimelineRoundedIcon />}
              onClick={() => setTimelineRow(row)}
              sx={{ color: "#44586A", fontWeight: 800 }}
            >
              Timeline
            </Button>
          ) : null}
          {canManageToolsRun && row.status === "needs_review" ? (
            <Button
              size="small"
              startIcon={<AutoFixHighRoundedIcon />}
              onClick={() => setResolveRow(row)}
              sx={{ color: "#A65F00", fontWeight: 800 }}
            >
              Resolve
            </Button>
          ) : null}
        </>
      )}
    />
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#F3F3F0",
        color: "#29313D",
      }}
    >
      <DocumentsTopNavbar title="Tools Tracker" />

      <Box sx={{ maxWidth: 1680, mx: "auto", p: { xs: 2, md: 3.5 } }}>
        {list}
      </Box>

      <ToolsRunUploadForm
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={(result) => {
          const importedClientId = result.rows.find(
            (row) => row.clientId,
          )?.clientId;
          if (importedClientId) {
            setClientId(String(importedClientId));
            fetchDataWells(importedClientId);
          }
          clearFilters();
          fetchData();
          setResolveRow(
            result.rows.find((row) => row.status === "needs_review") || null,
          );
        }}
      />
      <ToolsRunResolveDialog
        row={resolveRow}
        onClose={() => setResolveRow(null)}
        onResolved={fetchData}
      />
      <ToolsRunTimelineDrawer
        row={timelineRow}
        onClose={() => setTimelineRow(null)}
      />
    </Box>
  );
};
